import React, { useEffect, useMemo, useState } from 'react';
import { Spin, Typography, Button, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconEngine, IconGauge, IconClock, IconAlertTriangle, IconTruckOff } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import StaticMap from '../../components/StaticMap';
import { getMyHeavyDutyMachineries } from '../../services/HeavyDutyMachineryService';
import { getNotifications } from '../../services/NotificationService';
import { onTelemetryUpdate, onReceiveNotification } from '../../services/RealtimeService';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IFullMachineryTelemetry } from '../../interfaces/heavyDutyMachinery/ITelemetry';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { pillToneStyle, SURFACE_1_SOFT, CORAL } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

// A machine only ever gets recognized as "recently alerted" from a
// notification that's (a) type GeofenceAlert and (b) landed within this
// window -- older ones are still in the bell/notification list, just no
// longer flagged live on this card. Matched by contentTitle (the machine's
// own Title, per NotificationHelper's call in TelemetryIngestionService) --
// the notification payload carries no machineryId, so this is a best-effort
// visual signal, not an authoritative per-machine link.
const RECENT_ALERT_WINDOW_MS = 60 * 60 * 1000;

/**
 * "Mi flota en vivo" -- every one of the host's own machines, live position/
 * engine data via HeavyDutyMachineryHub's "TelemetryUpdate" push (see
 * RealtimeService.onTelemetryUpdate), keyed by payload.machineryId against
 * this list. getMyHeavyDutyMachineries() already seeds each machine's
 * `telemetry` with whatever the backend has cached at load time (see
 * MachineryController.GetMySpaces) -- the live subscription only ever
 * refines it from there, no polling needed.
 */
const HeavyDutyMachineryFleet: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [machines, setMachines] = useState<IHeavyDutyMachinery[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinct from machines.length === 0 -- see MyHeavyDutyMachineries's own
  // loadError doc comment for why a failed fetch must never render the same
  // empty state a host with a genuinely empty fleet sees.
  const [loadError, setLoadError] = useState(false);
  const [recentAlertTitles, setRecentAlertTitles] = useState<Set<string>>(new Set());

  const loadMachines = () => {
    setLoading(true);
    setLoadError(false);
    getMyHeavyDutyMachineries()
      .then(setMachines)
      .catch(() => {
        setLoadError(true);
        message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_LOAD_ERROR));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMachines();

    getNotifications(undefined, 30)
      .then((page) => {
        const cutoff = Date.now() - RECENT_ALERT_WINDOW_MS;
        const titles = page.items
          .filter((n) => n.type === 'GeofenceAlert' && n.contentTitle && new Date(n.createdAt).getTime() >= cutoff)
          .map((n) => n.contentTitle as string);
        setRecentAlertTitles(new Set(titles));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return onTelemetryUpdate((payload: IFullMachineryTelemetry) => {
      setMachines((prev) => prev.map((m) => (m.id === payload.machineryId ? { ...m, telemetry: payload } : m)));
    });
  }, []);

  useEffect(() => {
    return onReceiveNotification((notification) => {
      if (notification.type !== 'GeofenceAlert' || !notification.contentTitle) return;
      setRecentAlertTitles((prev) => new Set(prev).add(notification.contentTitle as string));
    });
  }, []);

  const cards = useMemo(
    () =>
      machines.map((machine) => {
        const telemetry = machine.telemetry as IFullMachineryTelemetry | undefined;
        const hasLiveData = !!telemetry && (telemetry.latitude != null || telemetry.engineHours != null || telemetry.ignitionOn != null);
        const hasAlert = recentAlertTitles.has(machine.title);

        return (
          <div
            key={machine.id}
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 16,
              padding: 16,
              marginBottom: 16,
              cursor: 'pointer',
            }}
            onClick={() => navigate(`/heavy-duty-machinery/${machine.id}`)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <Text strong style={{ fontSize: 15 }}>
                {machine.title}
              </Text>
              {hasAlert && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                    ...pillToneStyle('error', token),
                  }}
                >
                  <IconAlertTriangle size={12} />
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_GEOFENCE_ALERT_LABEL)}
                </span>
              )}
            </div>

            {!hasLiveData ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '14px 12px',
                  borderRadius: 12,
                  background: SURFACE_1_SOFT,
                  color: token.colorTextTertiary,
                }}
              >
                <IconTruckOff size={18} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_NO_LIVE_DATA)}
                </Text>
              </div>
            ) : (
              <>
                {telemetry?.latitude != null && telemetry?.longitude != null && (
                  <div style={{ marginBottom: 10 }}>
                    <StaticMap latitude={telemetry.latitude} longitude={telemetry.longitude} width={400} height={160} />
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 12px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      ...pillToneStyle(telemetry?.ignitionOn ? 'success' : 'default', token),
                    }}
                  >
                    <IconEngine size={14} />
                    {translate(
                      t,
                      telemetry?.ignitionOn
                        ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_ENGINE_ON
                        : TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_ENGINE_OFF
                    )}
                  </span>
                  {telemetry?.engineHours != null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: token.colorTextSecondary }}>
                      <IconClock size={14} />
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_ENGINE_HOURS_LABEL)}: {telemetry.engineHours.toFixed(1)}h
                    </span>
                  )}
                  {telemetry?.fuelPercent != null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: token.colorTextSecondary }}>
                      <IconGauge size={14} />
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_FUEL_LABEL)}: {Math.round(telemetry.fuelPercent)}%
                    </span>
                  )}
                </div>
                {telemetry?.lastUpdateUtc && (
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_LAST_UPDATE_LABEL)}: {new Date(telemetry.lastUpdateUtc).toLocaleString()}
                  </Text>
                )}
              </>
            )}
          </div>
        );
      }),
    [machines, recentAlertTitles, token, t, navigate]
  );

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 720 }}>
        <Title level={3} style={{ margin: '0 0 4px' }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_PAGE_TITLE)}
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 13 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_PAGE_SUBTITLE)}
        </Text>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : loadError ? (
          <EmptyState
            icon={<IconAlertTriangle size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_LOAD_ERROR)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_LOAD_ERROR_DESCRIPTION)}
            action={
              <Button shape="round" onClick={loadMachines}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_SPACES_RETRY_BUTTON)}
              </Button>
            }
          />
        ) : machines.length === 0 ? (
          <EmptyState
            icon={<IconEngine size={24} color={CORAL} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_PAGE_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_EMPTY)}
          />
        ) : (
          cards
        )}
      </div>
    </AppLayout>
  );
};

export default HeavyDutyMachineryFleet;
